import { matchPath } from 'react-router-dom';

// The portal's routes and the name each one goes by, and which of them an answer may name as a link.
//
// Its own module rather than part of routes.tsx because the assistant's Markdown renderer reads it, and
// routes.tsx lazy-imports every page in the portal — the assistant's own page included.

export const PAGE_TITLES: Record<string, string> = {
  '/': 'Home',
  '/calendar': 'Calendar',
  '/key-contacts': 'Key Contacts',
  '/agent-directory': 'Agent Directory',
  '/carrier-search': 'Carrier Search',
  '/request-a-carrier': 'Request a Carrier',
  '/make-payment': 'Make a Payment',
  '/carrier-payment-portals': 'Carrier Payment Portals',
  '/accounting': 'Accounting',
  '/agent-support': 'Agent Support',
  '/about-nsc': 'About NSC',
  '/search': 'Search Results',
  '/notifications': 'Notifications',
  '/news': 'News',
  '/news/new': 'Create News',
  '/events/new': 'Create Event',
  '/agency-management/my-agency': 'My Agency',
  '/agency-management/statements': 'Agency Statements',
  '/agency-management/agent-library': 'Agent Library',
  '/agency-management/va-resources': 'VA Resources',
  '/agency-management/equipment': 'Order Equipment',
  '/agency-management/operations': 'Agency Operations',
  '/agency-management/operations/recruitment-assistance': 'Recruitment Assistance',
  '/agency-management/operations/holiday-schedule': 'Holiday Schedule',
  '/agency-management/operations/storm-preparedness': 'Storm Preparedness',
  '/agency-management/operations/corporate-partnership-guide': 'Corporate Partnership Guide',
  '/agency-management/operations/operating-manual': 'Operating Manual',
  '/agency-management/operations/vendors': 'Vendors',
  '/marketing/branding': 'Branding',
  '/marketing/resources': 'Marketing Resources',
  '/marketing/canva': 'Canva',
  '/marketing/canva/ai-prompts': 'Canva AI Prompts',
  '/technology/feature-requests': 'Feature Requests',
  '/new-grad-roadmap': 'New Grad Roadmap',
  '/my-onboarding': 'My Onboarding',
  '/services/mortgage-lienholder-request': 'Mortgage/Lienholder Request',
  '/services/commercial-certificate-request': 'Commercial Certificate Request',
  '/assistant': 'Ask AgencyOS',
  '/agent-tools': 'Agent Tools',
  '/agent-tools/rater': 'Comparative Rater',
  '/agent-tools/rater/quotes/:quoteId': 'Rater Quote',
  '/self-service': 'Self-service',
  '/self-service/activities': 'My Activities',
  '/self-service/insureds/:insuredId': 'Customer',
  '/self-service/policy-uploads/:batchId': 'Policy Upload',
  '/self-service/policy-uploads/:batchId/review/:documentId': 'Review Policy',
  '/settings': 'My Settings',
  '/settings/call-capture': 'Zoom Call Capture',
  '/settings/notifications': 'Notification Settings',
  '/internal-tools/hr': 'HR',
  '/internal-tools/hr/dashboard': 'HR Analytics Dashboard',
  '/admin/users': 'User Management',
  '/admin/assistant': 'Assistant Prompt',
  '/admin/agency-redirects': 'Agency Redirects',
  '/admin/agency-redirects/new': 'New Agency Redirect',
  '/admin/control-center': 'API Control Center',
  '/admin/data-viewer': 'Data Viewer',
  '/admin/headshots': 'Bulk Headshots',
  '/admin/logs': 'Logs',
  '/admin/underwriting': 'Underwriting Guidelines',
  '/admin/branches/edit': 'Branch Management',
  '/admin/documents': 'Documents',
  '/admin/documents/list': 'Documents',
  '/admin/documents/upload': 'Upload Document',
  '/admin/documents/view/:documentId': 'Document',
  '/admin/documents/types': 'Document Types',
  '/admin/documents/types/new': 'New Document Type',
  '/admin/documents/types/:typeId': 'Edit Document Type',
};

// The title of the page at a pathname: an exact entry, or else the parameterised entry it matches
// (/self-service/insureds/123 is a Customer page).
export function pageTitleFor(pathname: string): string | undefined {
  const exact = PAGE_TITLES[pathname];
  if (exact) return exact;
  const pattern = Object.keys(PAGE_TITLES).find((p) => p.includes('/:') && matchPath(p, pathname));
  return pattern ? PAGE_TITLES[pattern] : undefined;
}

// Routes behind a grant most agents do not hold. A link to one offers a page that answers with a
// permission error, which is why the assistant's corpus leaves them out of the index as well.
export const GATED_PREFIXES = ['/admin', '/internal-tools/hr', '/self-service', '/agent-tools/rater', '/agency-management/statements'];

// The name of the page at a path an answer may link to, or undefined when it is not one. Parameterised
// routes above never match: a path written in prose is literal, and a link to the pattern is not openable.
export function linkablePageTitle(path: string): string | undefined {
  if (GATED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) return undefined;
  return PAGE_TITLES[path];
}
